import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'fit';

const VARIANT_CLASS: Record<Variant, string> = {
  primary: 'btn-glass btn-primary',
  secondary: 'btn-glass btn-secondary',
  ghost:
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold text-foreground transition hover:bg-muted',
  destructive:
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-destructive font-semibold text-destructive-foreground transition hover:opacity-90',
};

// Story 2-147qa — explicit control-height tokens (tokens.css
// `--control-h-sm`/`--control-h-md`, via `h-control-sm`/`h-control` here),
// not padding+line-height arithmetic. Visually a no-op for Button itself —
// `px-3 py-1.5 text-xs` already rendered 28px and `px-4 py-2 text-sm`
// already rendered 36px, exactly `--control-h-sm`/`--control-h-md`'s own
// values — but now every control on this scale (IconButton, Input, Select,
// SegmentedControl) traces to the SAME two tokens instead of each
// reimplementing the arithmetic that happens to land on the same number.
// `py-0` is REQUIRED, not decorative: `.btn-glass` (index.css, consumed by
// the primary/secondary variants) sets its own `padding: 0.65rem 1.4rem` —
// with no `py-*` utility here to override it (Tailwind's utilities layer
// beats `@layer components`), that 10.4px top+bottom padding would still
// apply ON TOP of the new fixed `height`, pushing the button's required
// content height (~40.8px) past the 36px box and breaking the very
// convergence this token exists to guarantee — caught live via the
// `story-2-134-visual-baseline` gallery screenshot (Buttons section grew
// 223px → 227px) while verifying this story.
// `fit` (story 2-147xd) — the one size that does NOT opt into the fixed
// control-height scale above: for a caller whose content genuinely cannot
// live inside a fixed `h-control-sm`/`h-control` box (multi-line wrapped
// text, a non-standard FAB-shaped launcher) and would visually clip if
// forced onto it — real cases, not a general opt-out: `AgentPanel.tsx`'s
// multi-line suggestion-chip buttons and its own fixed-position launcher
// pill, converted off raw `<button>` by this story. Contributes NO sizing
// classes at all — the caller's own `className` fully owns height/padding/
// text-size, the same "override via className" pattern `Button`'s own
// `ghost`-variant callers already use for a custom border-radius.
const SIZE_CLASS: Record<Size, string> = {
  sm: 'h-control-sm px-3 py-0 text-xs',
  md: 'h-control px-4 py-0 text-sm',
  fit: '',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Shows a spinner in place of the icon and disables the button. */
  loading?: boolean;
  /** Icon rendered alongside (or, with `iconOnly`, instead of) the label. */
  icon?: ReactNode;
  /** Which side of the label the icon renders on. Defaults to `left`. */
  iconPlacement?: 'left' | 'right';
  /** Renders only the icon; `children` stays the accessible name (visually hidden). */
  iconOnly?: boolean;
  /** Fills the width of the containing block instead of sizing to content. */
  block?: boolean;
  /** Toggle-button state — sets `aria-pressed`. Omit for non-toggle buttons. */
  pressed?: boolean | undefined;
}

// Canonical button — the btn-glass vocabulary as a typed component.
// Labels never wrap (pill buttons keep one line; the container wraps).
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    type = 'button',
    className = '',
    loading = false,
    icon,
    iconPlacement = 'left',
    iconOnly = false,
    block = false,
    pressed,
    disabled,
    children,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const displayIcon = loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : icon;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-pressed={pressed}
      aria-busy={loading || undefined}
      className={`focus-ring ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${block ? 'flex w-full' : ''} disabled:pointer-events-none disabled:opacity-50 ${className}`}
      {...rest}
    >
      {displayIcon && iconPlacement === 'left' && (
        <span aria-hidden="true" className="inline-flex shrink-0">
          {displayIcon}
        </span>
      )}
      <span className={iconOnly ? 'sr-only' : undefined}>{children}</span>
      {displayIcon && iconPlacement === 'right' && (
        <span aria-hidden="true" className="inline-flex shrink-0">
          {displayIcon}
        </span>
      )}
    </button>
  );
});
